/**
 * Dashboards -> AI usage tab, and the section's landing view -- it is the
 * first dashboard to register, so it is what the header's "Dashboards" link
 * opens (see the DASHBOARDS registry contract in @utils/navigation).
 *
 * Chrome -- the outer page wrapper, the centered container, the <h1> -- comes
 * from SectionLayout, the same as every other tab in a consolidated section.
 * This file renders only the tab's own content.
 *
 * Every developer sees their own usage; the backend scopes what comes
 * back (`resolve_scope` -- admin sees everything, a project Maintainer
 * sees their projects, everyone else sees only themselves). That's why
 * the route is a plain `ProtectedRoute`, not `AdminRoute`.
 *
 * Data shape notes (verified against `aiUsageService.ts` and
 * `backend/features/ai_gateway/schemas.py`):
 *  - `SummaryResponse` is `{ rows, scope_degraded }` -- no `totals`. The
 *    table footer sums the rows actually returned and labels itself as
 *    such (see `UsageTable`).
 *  - There is no price-provenance field anywhere in the API. The mockup's
 *    contract/effective/catalog/bundled chips are not built.
 *  - `group_by` only whitelists user/project/model/provider/client server-side,
 *    and there is no provider/user filter param -- this page always
 *    fetches at the finest granularity and does filtering/regrouping for
 *    the table client-side (see `usageAggregation.ts`).
 */

import { useMemo, useState, type ReactElement } from 'react';
import { useSummary, useTimeseries } from '@features/observability/hooks/useAiUsage';
import { UsageFilters } from '@components/features/aiUsage/UsageFilters';
import { UsageTiles } from '@components/features/aiUsage/UsageTiles';
import { UsageChart } from '@components/features/aiUsage/UsageChart';
import { UsageTable } from '@components/features/aiUsage/UsageTable';
import { ScopeBanner } from '@components/features/aiUsage/ScopeBanner';
import { filterRows, groupRows, toDisplayRow } from '@components/features/aiUsage/usageAggregation';
import { USAGE_DIMENSIONS, type UsageDimension } from '@components/features/aiUsage/types';

const ALL_DIMENSIONS: UsageDimension[] = [...USAGE_DIMENSIONS];
const DIMENSION_LABELS: Record<UsageDimension, string> = {
  user: 'User',
  project: 'Project',
  model: 'Model',
  provider: 'Provider',
  client: 'Client',
};

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AiUsageTab(): ReactElement {
  const [presetDays, setPresetDays] = useState(30);
  const [project, setProject] = useState('');
  const [provider, setProvider] = useState('');
  const [user, setUser] = useState('');
  const [client, setClient] = useState('');
  const [groupBy, setGroupBy] = useState<UsageDimension[]>(['user', 'project']);

  const range = useMemo(() => ({ start: isoDaysAgo(presetDays), end: todayIso() }), [presetDays]);

  const summary = useSummary({ start: range.start, end: range.end, groupBy: ALL_DIMENSIONS });
  const timeseries = useTimeseries({ start: range.start, end: range.end });

  const filteredRows = useMemo(() => {
    const wide = (summary.data?.rows ?? []).map(toDisplayRow);
    return filterRows(wide, { project, provider, user, client });
  }, [summary.data, project, provider, user, client]);

  const tableRows = useMemo(() => groupRows(filteredRows, groupBy), [filteredRows, groupBy]);

  const scopeDegraded = Boolean(summary.data?.scope_degraded || timeseries.data?.scope_degraded);

  function toggleDimension(dim: UsageDimension): void {
    setGroupBy((prev) => {
      if (prev.includes(dim)) {
        // Never let it drop to zero dimensions -- there'd be nothing to
        // group the table by.
        return prev.length === 1 ? prev : prev.filter((d) => d !== dim);
      }
      return [...prev, dim];
    });
  }

  return (
    <>
      <p className="section-description">
        Token and cost usage across AI providers. You always see your own; project
        maintainers also see their projects, and admins see the whole installation.
      </p>

      <ScopeBanner degraded={scopeDegraded} />

      <UsageFilters
        presetDays={presetDays}
        onPresetDaysChange={setPresetDays}
        project={project}
        onProjectChange={setProject}
        provider={provider}
        onProviderChange={setProvider}
        user={user}
        onUserChange={setUser}
        client={client}
        onClientChange={setClient}
      />

      {summary.isLoading ? (
        <p>Loading usage…</p>
      ) : summary.error ? (
        <p role="alert">Could not load usage data.</p>
      ) : (
        <UsageTiles rows={filteredRows} rangeLabel={`${String(presetDays)} days`} />
      )}

      <section className="usage-panel">
        <div className="usage-panel__head">
          <h2>Usage over time</h2>
        </div>
        <div className="usage-panel__body">
          {timeseries.isLoading ? (
            <p>Loading chart…</p>
          ) : timeseries.error ? (
            <p role="alert">Could not load the chart.</p>
          ) : (
            <UsageChart points={timeseries.data?.points ?? []} />
          )}
        </div>
      </section>

      <section className="usage-panel">
        <div className="usage-panel__head">
          <h2>Breakdown</h2>
          <div className="usage-chips">
            <span className="usage-chips__label">Group by</span>
            {ALL_DIMENSIONS.map((dim) => (
              <button
                key={dim}
                type="button"
                className="usage-chip"
                aria-pressed={groupBy.includes(dim)}
                onClick={() => { toggleDimension(dim); }}
              >
                {DIMENSION_LABELS[dim]}
              </button>
            ))}
          </div>
        </div>
        <div className="usage-panel__body">
          {summary.isLoading ? (
            <p>Loading breakdown…</p>
          ) : summary.error ? (
            <p role="alert">Could not load the breakdown.</p>
          ) : (
            <UsageTable rows={tableRows} groupBy={groupBy} />
          )}
        </div>
      </section>
    </>
  );
}
